import {
  columnFacetingFeature,
  columnFilteringFeature,
  createColumnHelper,
  createFacetedUniqueValues,
  createFilteredRowModel,
  createSortedRowModel,
  filterFns,
  globalFilteringFeature,
  rowSortingFeature,
  sortFns,
  tableFeatures,
  useTable,
  type ColumnDef,
  type RowData,
  type SortingState,
} from "@tanstack/react-table";
import {
  Input,
  NativeSelect,
  NativeSelectOption,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  cn,
} from "@portal/ui";
import { ArrowDown, ArrowUp, ArrowUpDown, Search } from "lucide-react";
import { useMemo, type ReactNode } from "react";
import { useRowNavigation } from "./rowClick";

/**
 * 一覧の共通部品(SPEC §14.1: すべての一覧で検索・絞り込み・並べ替えができる。ユーザー指示 2026-09-14)。
 * TanStack Table v9(SPEC §15.4 の候補として採用)+ shadcn/ui の Table。
 * - 検索: 文字列・数値のアクセサ列すべてを対象にした部分一致(表示専用の列は対象外)
 * - 絞り込み: 指定した列の値で完全一致(選択肢は列の値から自動で作る)
 * - 並べ替え: 見出しクリックで昇順 → 降順 → なし(`enableSorting: false` の列は不可)
 * 件数は多くても数百件の想定なので、すべてクライアント側で行う(SPEC §15.4「一覧(大量)」)。
 */
export const dataTableFeatures = tableFeatures({
  rowSortingFeature,
  sortedRowModel: createSortedRowModel(),
  sortFns,
  columnFilteringFeature,
  globalFilteringFeature,
  filteredRowModel: createFilteredRowModel(),
  filterFns,
  columnFacetingFeature,
  facetedUniqueValues: createFacetedUniqueValues(),
});
export type DataTableFeatures = typeof dataTableFeatures;

/** 各画面で列を定義するためのヘルパー。`helper.accessor` は検索・絞り込み・並べ替えの対象、`helper.display` は対象外。 */
export function createDataTableColumns<TData extends RowData>() {
  return createColumnHelper<DataTableFeatures, TData>();
}

export interface DataTableFilter {
  /** 絞り込む列の id(accessor のキーか、`id` を指定した列) */
  columnId: string;
  /** select の aria-label と「すべて」の前に付ける語 */
  label: string;
  /** 省略時は列の値から自動で作る(値の昇順) */
  options?: readonly { value: string; label: string }[];
}

/** 列ごとの見た目(幅・文字色など)。列定義の `meta` に渡す */
export interface DataTableColumnMeta {
  className?: string;
}
const metaOf = (meta: unknown) => (meta ?? {}) as DataTableColumnMeta;

export interface DataTableProps<TData extends RowData> {
  // helper.columns() の戻り値(列ごとに値の型が違う)を受けるため any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ColumnDef<DataTableFeatures, TData, any>[];
  data: TData[];
  getRowId: (row: TData) => string;
  /** 空の行がないときに出す文言 */
  emptyText: string;
  searchPlaceholder?: string;
  filters?: readonly DataTableFilter[] | undefined;
  initialSorting?: SortingState | undefined;
  /** ツールバーの右側に置く要素(登録ボタンなど) */
  toolbarEnd?: ReactNode;
  /** 行全体をクリックで開くプロジェクトの id(指定した一覧だけ) */
  rowProjectId?: ((row: TData) => string) | undefined;
  "data-testid"?: string;
}

const EMPTY: never[] = [];

export function DataTable<TData extends RowData>({
  columns,
  data,
  getRowId,
  emptyText,
  searchPlaceholder = "検索",
  filters = EMPTY,
  initialSorting,
  toolbarEnd,
  rowProjectId,
  ...rest
}: DataTableProps<TData>) {
  const rowNav = useRowNavigation();
  const table = useTable(
    {
      features: dataTableFeatures,
      columns,
      data,
      getRowId,
      globalFilterFn: "includesString",
      // 数値列も最初のクリックは昇順にそろえる
      sortDescFirst: false,
      initialState: initialSorting ? { sorting: initialSorting } : {},
    },
    // 再描画のきっかけにする state。並べ替えも含めないと見出しクリックで描画されない
    (state) => ({
      globalFilter: state.globalFilter,
      columnFilters: state.columnFilters,
      sorting: state.sorting,
    }),
  );
  const rows = table.getRowModel().rows;
  const filterOptions = useMemo(
    () =>
      filters.map((f) => {
        if (f.options) return { ...f, options: f.options };
        const column = table.getColumn(f.columnId);
        const values = column
          ? [...column.getFacetedUniqueValues().keys()]
              .filter((v): v is string => typeof v === "string" && v !== "")
              .sort((a, b) => a.localeCompare(b, "ja"))
          : [];
        return { ...f, options: values.map((v) => ({ value: v, label: v })) };
      }),
    // 絞り込みの選択肢は元データが変わったときだけ作り直す
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filters, data],
  );

  return (
    <div className="flex flex-col gap-3" data-testid={rest["data-testid"]}>
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            type="search"
            aria-label="検索"
            placeholder={searchPlaceholder}
            value={table.state.globalFilter ?? ""}
            onChange={(e) => table.setGlobalFilter(e.target.value)}
            className="w-64 pl-8"
          />
        </div>
        {filterOptions.map((f) => {
          const column = table.getColumn(f.columnId);
          const value = (column?.getFilterValue() as string | undefined) ?? "";
          return (
            <NativeSelect
              key={f.columnId}
              aria-label={`${f.label}で絞り込み`}
              value={value}
              onChange={(e) => column?.setFilterValue(e.target.value || undefined)}
            >
              <NativeSelectOption value="">{f.label}: すべて</NativeSelectOption>
              {f.options.map((o) => (
                <NativeSelectOption key={o.value} value={o.value}>
                  {o.label}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          );
        })}
        {toolbarEnd && <div className="ml-auto flex items-center gap-2">{toolbarEnd}</div>}
      </div>
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((group) => (
            <TableRow key={group.id}>
              {group.headers.map((header) => {
                const canSort = header.column.getCanSort();
                const sorted = header.column.getIsSorted();
                const size = metaOf(header.column.columnDef.meta).className;
                return (
                  <TableHead key={header.id} className={cn(size)}>
                    {header.isPlaceholder ? null : canSort ? (
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 hover:text-foreground"
                        onClick={header.column.getToggleSortingHandler()}
                        aria-sort={
                          sorted === "asc" ? "ascending" : sorted === "desc" ? "descending" : "none"
                        }
                      >
                        <table.FlexRender header={header} />
                        {sorted === "asc" ? (
                          <ArrowUp className="size-3.5" aria-label="昇順" />
                        ) : sorted === "desc" ? (
                          <ArrowDown className="size-3.5" aria-label="降順" />
                        ) : (
                          <ArrowUpDown className="size-3.5 opacity-40" aria-hidden="true" />
                        )}
                      </button>
                    ) : (
                      <table.FlexRender header={header} />
                    )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={columns.length} className="text-muted-foreground">
                {data.length === 0 ? emptyText : "条件に合う行はありません。"}
              </TableCell>
            </TableRow>
          )}
          {rows.map((row) => (
            <TableRow
              key={row.id}
              {...(rowProjectId ? rowNav(rowProjectId(row.original)) : {})}
              tabIndex={rowProjectId ? 0 : undefined}
              className={cn(rowProjectId && "cursor-pointer")}
            >
              {row.getAllCells().map((cell) => (
                <TableCell
                  key={cell.id}
                  className={cn(metaOf(cell.column.columnDef.meta).className)}
                >
                  <table.FlexRender cell={cell} />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
